import { getGatedIdacBackendUrl } from '../utilities/env';
import { ThunkAction, URLParameters } from '../types';
import * as queryString from 'query-string';
import { AnalyticsClient } from '../utilities/analytics/analytics-web-client';
import { captureTaggedException } from '../utilities/analytics/error-reporting';
import {
  verifyEmailOtpFailure,
  verifyEmailOtpRequest,
  verifyEmailOtpSuccess,
} from './verify-email-otp-actions';

export interface InitiateOtpPayload {
  token?: string;
}

/**
 * Triggers sending the verification code (OTP) email for the invite flow.
 *
 * Historically the OTP email was sent automatically when the user clicked the
 * invite link. To reduce unsolicited emails (CYBER-403603) the send is now
 * deferred behind an interstitial page: the user clicks "Send verification code
 * email", which calls this action. On success the backend sends the OTP and
 * returns the verify-email-otp redirect, which we navigate to.
 *
 * The POST preserves the current page's query string (token + destination /
 * cobranding params) so sign-in-service can build the OTP link with the user's
 * continue destination intact.
 *
 * This action reuses the `verifyEmailOtp` redux slice (isLoading / error /
 * redirectTo) since the interstitial and the OTP entry page share it.
 */
export const initiateOtp =
  (pageId: string, analyticsClient: AnalyticsClient): ThunkAction =>
  async (dispatch, getState) => {
    const {
      csrfToken,
      verifyEmailOtp: { redirectType },
    } = getState();
    const { token }: URLParameters = queryString.parse(window.location.search);

    // sign-in-service reads the token from the request body; the query string is appended to the URL
    // (below) only to carry the destination / cobranding params. The token therefore appears in both
    // places, in the body because that is where the endpoint reads it, and incidentally in the query.
    const payload: InitiateOtpPayload = {
      token,
    };

    dispatch(verifyEmailOtpRequest());

    const path = getGatedIdacBackendUrl(
      `/rest/signup/verify-email/initiate-otp${window.location.search}`
    );

    return fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
      .then(response =>
        response.json().then(({ error, redirectTo }) => {
          if (redirectTo) {
            analyticsClient.trackingEvent({
              page: pageId,
              action: 'submitted',
              subject: 'initiate_otp',
              attributes: { result: 'success', signupType: redirectType },
            });

            dispatch(verifyEmailOtpSuccess(redirectTo));
          } else {
            const result = error || 'internal_server_error';
            dispatch(verifyEmailOtpFailure(result));
            captureTaggedException(error, {
              capabilities: ['capability-group--signup', 'capability-name--verify-email'],
              tags: { otp: 'initiate_otp' },
            });
            analyticsClient.trackingEvent({
              page: pageId,
              action: 'submitted',
              subject: 'initiate_otp',
              attributes: { result, signupType: redirectType },
            });
          }
        })
      )
      .catch(err => {
        dispatch(verifyEmailOtpFailure('unexpected_error'));
        captureTaggedException(err, {
          capabilities: ['capability-group--signup', 'capability-name--verify-email'],
          tags: { otp: 'initiate_otp' },
        });
        analyticsClient.trackingEvent({
          page: pageId,
          action: 'submitted',
          subject: 'initiate_otp',
          attributes: { result: 'unexpected_error', signupType: redirectType },
        });
      });
  };
